import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faPlus,
  faEdit,
  faArchive,
  faBoxOpen,
  faFileUpload,
  faFile,
  faImage,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DocumentModal from './DocumentModal';
import DocumentTypesList from '../Dictionary/TypeDocuments/DocumentTypesList';
import '../QueueFull/cabinet-zdo-requests.css';

import './documents.css';

// Импорт API сервисов
import { 
  listDocsKG, 
  createNewDoc_KG, 
  removeDoc_KG,
  updateDocInfo,
  restoreDoc_KG,
  getDictionary 
} from './actions';

const DocumentsList = ({ kgId = '' }) => {
  const [activeTab, setActiveTab] = useState('dismissal_order');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typesDocuments, setTypesDocuments] = useState([]);

  // Загрузка документов при монтировании и изменении таба
  useEffect(() => {
    loadDocuments(activeTab);
  }, [kgId, activeTab]);

  useEffect(()=>{
    async function dictionarySet() {
      const ret = await getDictionary();
      const arr_types = ret.map(r=>({value: r.name, description: r.description, filter_by: 'type'}));
      arr_types.push({ value: 'dismissal_order', description: 'Наказ про вибуття', filter_by: 'type', transl: 'Наказ про вибуття' });
      arr_types.push({ value: 'transfer_order', description: 'Наказ про переведення', filter_by: 'type', transl: 'Наказ про переведення' });
      arr_types.push({ value: 'archived', description: 'Архів', filter_by: 'status', transl: 'Архів' });
      

      setTypesDocuments(arr_types);
    }
    if(typesDocuments.length == 0) dictionarySet();
  }, [])



  const loadDocuments = async (tab) => {
    try {
      setLoading(true);
      const req_obj = {kgId};

      //if(tab == 'orderout') req_obj['type'] = 'dismissal_order';
      //if(tab == 'archived') req_obj['status'] = 'archived';
      //if(tab !== 'orderout') req_obj['no_type'] = 'dismissal_order';

      const obj_type = typesDocuments.find(t=>t.value == tab);

      if(obj_type?.filter_by) req_obj[obj_type?.filter_by] = tab;

      const result = await listDocsKG(req_obj);
      
      if (result && result.data) {
        const transformedData = result.data.map(doc => ({
          id: doc._id,
          name: doc.name,
          type: doc.type,
          description: doc.description,
          fileCount: doc.files ? doc.files.length : 0,
          totalSize: calculateTotalSize(doc.files),
          createdAt: formatDate(doc.createdAt),
          updatedAt: formatDate(doc.updatedAt),
          status: doc.status?.active ? 'active' : 'archived',
          files: transformFiles(doc.files)
        }));
        
        setDocuments(transformedData);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      //toast.error('Помилка завантаження документів');
    } finally {
      setLoading(false);
    }
  };

  // Функция для обновления документов (будет передана в модалку)
  const handleDocumentsUpdate = () => {
    loadDocuments(activeTab);
  };

  const calculateTotalSize = (files) => {
    if (!files || !files.length) return '0 Б';
    
    const totalBytes = files.reduce((sum, file) => sum + (file.length || 0), 0);
    
    if (totalBytes < 1024) return `${totalBytes} Б`;
    if (totalBytes < 1024 * 1024) return `${(totalBytes / 1024).toFixed(1)} КБ`;
    return `${(totalBytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('uk-UA');
  };

  const transformFiles = (files) => {
    if (!files || !files.length) return [];
    
    return files
      .filter(file => !file.status?.removed)
      .map(file => ({
        id: file._id,
        name: file.name,
        type: getFileType(file.mtype),
        size: formatFileSize(file.length),
        uploadedAt: formatDate(file.date_upload || file.date),
        status: file.status?.trash ? 'trash' : 'active',
        is_public: file.is_public,
        mtype: file.mtype
      }));
  };

  const getFileType = (mtype) => {
    if (mtype?.includes('image')) return 'image';
    if (mtype?.includes('pdf')) return 'pdf';
    if (mtype?.includes('word') || mtype?.includes('doc')) return 'docx';
    if (mtype?.includes('excel') || mtype?.includes('sheet')) return 'xlsx';
    return 'file';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Б';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  const columns = [
    {
      key: 'name',
      label: 'Назва',
      sortable: true,
      width: '25%'
    },
    {
      key: 'type',
      label: 'Найменування документу',
      sortable: true,
      width: '15%'
    },
    {
      key: 'description',
      label: 'Додатковий опис',
      sortable: true,
      width: '25%'
    },
    {
      key: 'fileCount',
      label: 'Кількість файлів',
      sortable: true,
      width: '10%'
    },
    {
      key: 'totalSize',
      label: 'Розмір',
      sortable: true,
      width: '10%'
    },
    {
      key: 'createdAt',
      label: 'Дата завантаження',
      sortable: true,
      width: '10%'
    },
    {
      key: 'actions',
      label: 'Дії',
      sortable: false,
      width: '10%'
    }
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return documents;
    
    return [...documents].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [documents, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index, e) => {
    e.stopPropagation();
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

   const handleActionSelect = async (actionId, document) => {
    try {
      switch (actionId) {
        case 'edit':
          setSelectedDocument(document);
          setIsModalOpen(true);
          break;
          
        case 'add_file':
          setSelectedDocument(document);
          setIsModalOpen(true);
          break;
          
        case 'archive':
          await removeDoc_KG(document.id);
          //toast.success('Документ відправлено до архіву');
          await loadDocuments(activeTab); // Обновляем список
          break;
          
        case 'restore':
          await restoreDoc_KG(document.id);
          //toast.success('Документ поновлено з архіву');
          await loadDocuments(activeTab); // Обновляем список
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error('Error performing action:', error);
      //toast.error('Помилка виконання дії');
    }
    
    setActiveMenuIndex(null);
  };

  const handleAddDocument = () => {
    setSelectedDocument(null);
    setIsModalOpen(true);
  };

   const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
    loadDocuments(activeTab); // Перезагружаем список после закрытия модалки
  };

  const handleDocumentSave = async (documentData) => {
    try {
      if (selectedDocument) {
        await updateDocInfo(selectedDocument.id, documentData.name, documentData.description);
        //toast.success('Документ успішно оновлено');
      } else {
        await createNewDoc_KG({
          target_id: kgId,
          type: documentData.type,
          name: documentData.name,
          description: documentData.description
        });
        //toast.success('Документ успішно створено');
      }
      // Не нужно вызывать loadDocuments здесь, т.к. handleCloseModal уже вызовет его
    } catch (error) {
      console.error('Error saving document:', error);
      //toast.error('Помилка збереження документа');
      throw error; // Пробрасываем ошибку, чтобы модалка не закрывалась
    }
  };

  const ActionsMenu = ({ onActionSelect, onClose, document, isArchived }) => {
    const actions = isArchived 
      ? [
          {
            id: 'edit',
            label: 'Редагувати',
            icon: faEdit
          },
          {
            id: 'restore',
            label: 'Поновити з архіву',
            icon: faBoxOpen
          }
        ]
      : [
          {
            id: 'edit',
            label: 'Редагувати',
            icon: faEdit
          },
          {
            id: 'add_file',
            label: 'Додати файл',
            icon: faFileUpload
          },
          {
            id: 'archive',
            label: 'Відправити до архіву',
            icon: faArchive
          }
        ];

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, document)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  const TabsPanel = () => {

    return (
      <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label text-white-50">Файли документів та фото</span>
                <ul className="queuetable-year-tabs nav nav-tabs">
                {typesDocuments?.map(t=>{
                  return (
                    <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === t.value ? 'active' : ''}`}
                      onClick={() => setActiveTab(t.value)}
                    >
                      {t.transl || t.value}
                    </button>
                  </li>
                  )
                })}
                </ul>
              </div>
              <div className="queuetable-switch-wrap">
                <a 
                  href="" 
                  className="link-primary small text-nowrap" 
                  onClick={(e) => { e.preventDefault(); handleAddDocument(); }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Створити документ
                </a>
              </div>
      </div>
    )

    return (
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label text-white-50">Документи</span>
                <ul className="queuetable-year-tabs nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                      onClick={() => setActiveTab('active')}
                    >
                      Загальні
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'orderout' ? 'active' : ''}`}
                      onClick={() => setActiveTab('orderout')}
                    >
                      Накази про вибуття
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'archived' ? 'active' : ''}`}
                      onClick={() => setActiveTab('archived')}
                    >
                      Архів
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'type_docs' ? 'active' : ''}`}
                      onClick={() => setActiveTab('type_docs')}
                    >
                      Типи документів
                    </button>
                  </li>
                </ul>
              </div>
              <div className="queuetable-switch-wrap">
                <a 
                  href="" 
                  className="link-primary small text-nowrap" 
                  onClick={(e) => { e.preventDefault(); handleAddDocument(); }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Додати документ
                </a>
              </div>
            </div>
    );
  }

  if(false && activeTab == 'type_docs') {

    return (
      <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табов */}
            <TabsPanel />
            <DocumentTypesList isShowTabs={false} isEditPossible={false} />
          </div>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табов */}
            <TabsPanel />
            {/* Таблица */}
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Завантаження...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table queuetable-table align-middle mb-0">
                        <thead>
                          <tr>
                            {columns.map((column) => (
                              <th 
                                key={column.key}
                                style={{ width: column.width }}
                                onClick={() => column.sortable && handleSort(column.key)}
                              >
                                <div className="d-flex align-items-center">
                                  {column.label}
                                  {column.sortable && (
                                    <FontAwesomeIcon 
                                      icon={getSortIcon(column.key)} 
                                      className="ms-1" 
                                      style={{ fontSize: '12px' }}
                                    />
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData.map((document, index) => {
                            
                            const typeDoc = typesDocuments.find(t=>t.value == document.type) || {};

                            return (
                            <tr key={document.id}>
                              <td>{document.name}</td>
                              <td>{typeDoc.transl || typeDoc.value}</td>
                              <td>{document.description}</td>
                              <td>{document.fileCount}</td>
                              <td>{document.totalSize}</td>
                              <td>{document.createdAt}</td>
                              <td className="queuetable-actions text-end">
                                <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                  <button
                                    className="btn btn-sm btn-link text-decoration-none p-0"
                                    onClick={(e) => handleMenuToggle(index, e)}
                                    style={{ color: '#0d6efd' }}
                                  >
                                    <FontAwesomeIcon icon={faEllipsisVertical} />
                                  </button>
                                  
                                  {activeMenuIndex === index && (
                                    <ActionsMenu 
                                      onActionSelect={handleActionSelect}
                                      onClose={handleMenuClose}
                                      document={document}
                                      isArchived={activeTab === 'archived'}
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          )})}
                        </tbody>
                      </table>
                    </div>

                    {/* Пагинация */}
                    <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                      <div className="queuetable-pagination-info">
                        Всього {sortedData.length} документів
                      </div>
                      <nav aria-label="Page navigation">
                        <ul className="pagination">
                          <li className="page-item">
                            <button className="page-link" aria-label="Previous">
                              <span aria-hidden="true">&lt;</span>
                            </button>
                          </li>
                          <li className="page-item active">
                            <button className="page-link">1</button>
                          </li>
                          <li className="page-item">
                            <button className="page-link">2</button>
                          </li>
                          <li className="page-item">
                            <button className="page-link">3</button>
                          </li>
                          <li className="page-item">
                            <button className="page-link" aria-label="Next">
                              <span aria-hidden="true">&gt;</span>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно редактирования документа */}
 <DocumentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        documentData={selectedDocument}
        onSave={handleDocumentSave}
        onDocumentsUpdate={handleDocumentsUpdate}
        typeDoc={typesDocuments.find(t=>t.value == activeTab)} // Передаем функцию обновления
      />

      {/* Toast уведомления */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default DocumentsList;